import React from 'react';
import { calculateReadinessDecay, ReadinessCalculationResult } from '../utils/competencyDecay';

interface CompetencyReadinessCardProps {
  baselineScore?: number;
  assessedAt?: string | Date;
  learnerName?: string;
  onRefreshAssessment?: () => void;
  isAdminView?: boolean;
}

export const CompetencyReadinessCard: React.FC<CompetencyReadinessCardProps> = ({
  baselineScore = 86.5,
  assessedAt = new Date(Date.now() - 34 * 24 * 60 * 60 * 1000).toISOString(), // 34 days ago default
  learnerName,
  onRefreshAssessment,
  isAdminView = false,
}) => {
  const result: ReadinessCalculationResult = calculateReadinessDecay({
    baselineScore,
    assessedAt,
  });

  return (
    <div
      style={{
        background: 'var(--white, #FFFFFF)',
        border: '1px solid var(--line, #E2E8F0)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
        display: 'none',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>🛡️</span>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--navy, #0F172A)' }}>
              {isAdminView ? (learnerName ? `${learnerName} - Cohort Competency Readiness & Decay Status` : 'Cohort Competency Readiness & Decay Status') : (learnerName ? `${learnerName}'s Job-Readiness & Certification Health` : 'My Job-Readiness & Certification Health')}
            </h3>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted, #64748B)' }}>
            Calculated from highest evaluated assessment. Decays 10% per 90 days elapsed (PRD §12.1).
          </p>
        </div>

        <div
          style={{
            background: `${result.statusColor}18`,
            border: `1px solid ${result.statusColor}40`,
            color: result.statusColor,
            padding: '5px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: result.statusColor }} />
          {result.statusLabel}
        </div>
      </div>

      {/* Metrics Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '14px',
          background: 'var(--surface, #F8FAFC)',
          border: '1px solid var(--line, #E2E8F0)',
          borderRadius: '12px',
          padding: '16px 20px',
        }}
      >
        <div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint, #94A3B8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Current Readiness
          </span>
          <div style={{ fontSize: '24px', fontWeight: 800, color: result.statusColor, marginTop: '2px' }}>
            {result.currentScore}%
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted, #64748B)' }}>
            -{result.decayPercentage}% over {result.daysElapsed} days
          </span>
        </div>

        <div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint, #94A3B8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Certified Baseline
          </span>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy, #0F172A)', marginTop: '2px' }}>
            {result.baselineScore}%
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted, #64748B)' }}>
            Initial Exam Score
          </span>
        </div>

        <div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint, #94A3B8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Cycle Remaining
          </span>
          <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--navy, #0F172A)', marginTop: '2px' }}>
            {result.daysRemainingInCycle} <span style={{ fontSize: '14px', fontWeight: 600 }}>days</span>
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted, #64748B)' }}>
            Until next 90-day step
          </span>
        </div>

        <div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--faint, #94A3B8)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Certification Grace
          </span>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#10B981', marginTop: '2px' }}>
            Active
          </div>
          <span style={{ fontSize: '11px', color: 'var(--muted, #64748B)' }}>
            Pass mark: 70.0%
          </span>
        </div>
      </div>

      {/* Progress & Milestone Bar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
          <span style={{ color: 'var(--muted, #64748B)' }}>Readiness Preservation Curve</span>
          <span style={{ color: result.statusColor }}>{result.currentScore}% / 100%</span>
        </div>
        <div style={{ height: '10px', background: '#E2E8F0', borderRadius: '5px', overflow: 'hidden', position: 'relative' }}>
          <div
            style={{
              width: `${result.currentScore}%`,
              height: '100%',
              background: `linear-gradient(90deg, #38BDF8, ${result.statusColor})`,
              borderRadius: '5px',
              transition: 'width 0.4s ease',
            }}
          />
          {/* Target passmark line at 70% */}
          <div
            title="70% Passing Threshold"
            style={{
              position: 'absolute',
              left: '70%',
              top: 0,
              bottom: 0,
              width: '2px',
              background: '#DC2626',
              zIndex: 2,
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: '#94A3B8', marginTop: '4px' }}>
          <span>0%</span>
          <span style={{ color: '#DC2626', fontWeight: 700 }}>▲ 70% Pass Standard</span>
          <span>100% Certified</span>
        </div>
      </div>

      {/* Footer Policy Note & Action */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          paddingTop: '8px',
          borderTop: '1px solid var(--line, #E2E8F0)',
        }}
      >
        <span style={{ fontSize: '12px', color: 'var(--muted, #64748B)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>🔒</span>
          <em>Practice attempts and targeted micro-drills do not alter certified readiness or reset the decay timer.</em>
        </span>

        {onRefreshAssessment && (
          <button
            type="button"
            onClick={onRefreshAssessment}
            className="admin-btn admin-btn--primary"
            style={{
              padding: '8px 18px',
              fontSize: '12.5px',
              fontWeight: 700,
              cursor: 'pointer',
              borderRadius: '8px',
            }}
          >
            🔄 Refresh Assessment (Reset Decay)
          </button>
        )}
      </div>
    </div>
  );
};
